// Who was in a meeting, as names a person reads. Calendars often send an
// attendee with no name ("jack.munsaka@…", "david@…") while another
// invite of the same person carries it ("Jack Munsaka"), so a name is
// first looked up across every loaded meeting (nameBook), then tidied
// from the address: a first.last handle reads as "First Last", a
// one-word handle as a first name ("David"), a shared mailbox ("hello@",
// "info@") as its organisation ("Hammerhead7"). Handles with digits
// ("thakrarjay12"), long run-together words ("alexanderspencer") and
// consonant runs ("mhsnjvd") stay as they are: guessing a name there would
// be wrong.
//
// The desktop Meetings page only. The phone and the iPhone app keep
// status.otherAttendees, which the shared fixtures pin.
//
// Pure functions, no React. Self-test: listModel.selftest.ts (people as names).

import type { Meeting } from "@/api/meetings";
import { joinList } from "@/lib/format";
import { personName } from "./status";

type Attendee = Meeting["attendees"][number];

/** Lower-cased email → the best name any invite gave that person. */
export type NameBook = ReadonlyMap<string, string>;

const hasSpace = (s: string) => /\s/.test(s.trim());

export function nameBook(meetings: readonly Pick<Meeting, "attendees">[]): NameBook {
  const book = new Map<string, string>();
  for (const m of meetings) {
    for (const a of m.attendees) {
      const email = a.email?.trim().toLowerCase();
      const name = a.name?.trim();
      if (!email || !name || name.includes("@")) continue;
      const tidy = personName(name);
      const have = book.get(email);
      // A full name beats a one-word one ("Jack Munsaka" over "Jack").
      if (!have || (!hasSpace(have) && hasSpace(tidy))) book.set(email, tidy);
    }
  }
  return book;
}

// Shared mailboxes: the person behind them is unknown, the organisation is not.
const ROLE_BOXES = new Set([
  "hello", "hi", "hey", "info", "contact", "team", "office", "admin", "support", "sales", "enquiries", "enquiry",
  "inquiries", "accounts", "billing", "mail", "careers", "jobs", "press", "partners", "partnerships", "invest",
  "investors", "founders", "ops", "operations", "general", "reception", "bookings", "events", "media", "marketing",
  "help", "service", "noreply", "no-reply", "studio", "management",
]);
const SECOND_LEVEL = new Set(["co", "com", "org", "net", "ac", "gov", "ltd", "plc", "edu"]);
const FIRST_LAST = /^[a-z]+(?:-[a-z]+)*(?:[._][a-z]+(?:-[a-z]+)*)+$/i;
const WORD = /^[a-z]+(?:-[a-z]+)*$/i;
const capital = (w: string) => w.charAt(0).toUpperCase() + w.slice(1);
const capitalWord = (w: string) => w.split("-").map((p) => capital(p.toLowerCase())).join("-");

/** "hammerhead7.com" → "Hammerhead7", "foo.co.uk" → "Foo". */
export function organisationOf(domain: string): string {
  const labels = domain.toLowerCase().split(".").filter(Boolean);
  if (labels.length < 2) return labels[0] ? capital(labels[0]) : "";
  const i = labels.length >= 3 && SECOND_LEVEL.has(labels[labels.length - 2]) ? labels.length - 3 : labels.length - 2;
  return capital(labels[i]);
}

/** A name from an address alone. */
export function nameFromEmail(email: string): string {
  const at = email.indexOf("@");
  const local = at < 0 ? email : email.slice(0, at);
  const domain = at < 0 ? "" : email.slice(at + 1);
  if (ROLE_BOXES.has(local.toLowerCase())) return organisationOf(domain) || email;
  if (FIRST_LAST.test(local)) return personName(local);
  // A short pronounceable word reads as a first name; a long one is usually
  // two run together ("alexanderspencer"), a consonant run an abbreviation.
  const nameLike = (w: string) => w.length > 1 && w.length <= 9 && !/[^aeiouy]{4}/i.test(w);
  if (WORD.test(local) && local.split("-").every(nameLike)) return capitalWord(local);
  return local || email;
}

/** One attendee as a name: theirs, else the book's, else from the address. */
export function attendeeName(a: Pick<Attendee, "name" | "email">, book: NameBook): string {
  const name = a.name?.trim() ?? "";
  if (name && !name.includes("@")) return personName(name);
  const email = (a.email ?? "").trim();
  const known = book.get(email.toLowerCase());
  if (known) return known;
  // Some calendars put the address in the name field (twice, even).
  return nameFromEmail(email || name.split(/\s+/)[0]);
}

/** The other people in the meeting, as names, each person once. */
export function peopleIn(m: Pick<Meeting, "attendees">, book: NameBook): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of m.attendees) {
    const email = a.email?.trim().toLowerCase();
    if (a.self || !email || seen.has(email)) continue;
    seen.add(email);
    out.push(attendeeName(a, book));
  }
  return out;
}

/**
 * "Hannah Green and Tom Parker", "Ido Garbi, Alex Coggins and 6 more";
 * with nobody else: "Recorded in the app" / "Notes bot recording" / "Just you".
 */
export function peopleLine(m: Pick<Meeting, "attendees" | "source">, book: NameBook, max = 2): string {
  const who = peopleIn(m, book);
  if (who.length) return who.length <= max + 1 ? joinList(who) : `${who.slice(0, max).join(", ")} and ${who.length - max} more`;
  if (m.source === "recording") return "Recorded in the app";
  if (m.source === "bot") return "Notes bot recording";
  return "Just you";
}

/** Everyone else's name and address, one per line, for a tooltip. */
export function peopleTooltip(m: Pick<Meeting, "attendees">, book: NameBook): string {
  return m.attendees
    .filter((a) => !a.self && a.email)
    .map((a) => {
      const n = attendeeName(a, book);
      return n.toLowerCase() === a.email.toLowerCase() ? a.email : `${n} <${a.email}>`;
    })
    .join("\n");
}
